import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { ArrowDownLeft, ArrowUpRight } from "lucide-react";
import { RootDispatch, RootState } from "../../../store";
import {
  createTransfer,
  acceptTransfer,
  cancelTransfer,
  fetchPendingIncomingTransfers,
  fetchPendingOutgoingTransfers,
  fetchIncomingSerials,
  fetchOutgoingSerials,
  clearError,
} from "../../../store/slices/StockTransfer/stockTransferSlice";
import { fetchWarehouseNamesAndIdsThunk } from "../../../store/slices/warehouseSlice";
import { toast } from "react-hot-toast";
import { CreateTransferPayload } from "../../../store/slices/StockTransfer/stockTransferSlice";

// Components
import CreateTransferSection from "./CreateTransferSection";
import IncomingTransferSection from "./IncomingTransferSection";
import OutgoingTransferSection from "./OutgoingTransferSection";
import MaterialTransferTab from "./MaterialTransfer/MaterialTransferTab";

const LoadingSpinner = () => (
  <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
);

const StockTransferTab: React.FC = () => {
  const dispatch = useDispatch<RootDispatch>();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeMasterTab =
    searchParams.get("transferCategory") === "material"
      ? "material"
      : "product";
  const activeTransferTab =
    searchParams.get("transferTab") === "outgoing" ? "outgoing" : "incoming";
  const {
    incoming,
    outgoing,
    incomingSerials,
    outgoingSerials,
    actionLoading,
    error,
  } = useSelector((s: RootState) => s.stockTransfer);
  const { warehouseNamesAndIds } = useSelector((s: RootState) => s.warehouse);
  const isAdmin = useSelector((s: RootState) => s.auth.role === "admin");
  const warehouseAssigned = useSelector(
    (s: RootState) => s.auth.warehouseAssigned,
  );

  useEffect(() => {
    dispatch(fetchWarehouseNamesAndIdsThunk());
  }, [dispatch]);

  useEffect(() => {
    if (activeMasterTab !== "product") return;
    dispatch(fetchPendingIncomingTransfers(1));
    dispatch(fetchPendingOutgoingTransfers(1));
    dispatch(fetchIncomingSerials());
    dispatch(fetchOutgoingSerials());

    return () => {
      dispatch(clearError());
    };
  }, [dispatch, activeMasterTab]);

  useEffect(() => {
    if (error) {
      toast.error(error);
      dispatch(clearError());
    }
  }, [error, dispatch]);

  const handleCreateBatch = async (payload: CreateTransferPayload) => {
    const result = await dispatch(createTransfer(payload));
    if (createTransfer.fulfilled.match(result)) {
      toast.success(
        `${payload.items.length} product transfer(s) created successfully`,
      );
      dispatch(fetchPendingOutgoingTransfers(1));
      dispatch(fetchOutgoingSerials());
    }
  };

  const handleAcceptBatch = async (
    items: Array<{ transferId: string; remark?: string }>,
  ): Promise<boolean> => {
    if (items.length === 0) return false;
    const result = await dispatch(acceptTransfer(items));
    if (acceptTransfer.fulfilled.match(result)) {
      toast.success(`${items.length} transfer(s) accepted`);
      dispatch(fetchIncomingSerials());
      dispatch(fetchOutgoingSerials());
      return true;
    }
    return false;
  };

  const handleCancelBatch = async (
    items: Array<{ transferId: string; remark?: string }>,
  ): Promise<boolean> => {
    if (items.length === 0) return false;
    const result = await dispatch(cancelTransfer(items));
    if (cancelTransfer.fulfilled.match(result)) {
      toast.success("Transfer(s) cancelled");
      dispatch(fetchIncomingSerials());
      dispatch(fetchOutgoingSerials());
      fetchPendingOutgoingTransfers(1);
      return true;
    }
    return false;
  };

  const warehouseOptions =
    warehouseNamesAndIds?.map((w) => ({ value: w._id, label: w.name })) || [];

  const changeTransferTab = (tab: "incoming" | "outgoing") => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set("transferTab", tab);
    setSearchParams(nextParams, { replace: true });
  };

  const changeMasterTab = (tab: "product" | "material") => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set("transferCategory", tab);
    setSearchParams(nextParams, { replace: true });
  };

  return (
    <div className="w-full space-y-6">
      <div
        className="grid w-full grid-cols-2 gap-2 rounded-xl border border-gray-200 bg-gray-50 p-1.5 sm:inline-flex sm:gap-1"
        role="tablist"
        aria-label="Transfer categories"
      >
        <button
          type="button"
          role="tab"
          id="product-transfer-tab"
          aria-selected={activeMasterTab === "product"}
          aria-controls="product-transfer-panel"
          onClick={() => changeMasterTab("product")}
          className={`min-h-12 rounded-lg px-4 py-2 text-sm font-semibold transition-colors sm:min-w-32 ${
            activeMasterTab === "product"
              ? "bg-white text-[#0077C0] shadow-sm"
              : "text-gray-500 hover:bg-white/70 hover:text-gray-800"
          }`}
        >
          Product
        </button>
        <button
          type="button"
          role="tab"
          id="material-transfer-tab"
          aria-selected={activeMasterTab === "material"}
          aria-controls="material-transfer-panel"
          onClick={() => changeMasterTab("material")}
          className={`min-h-12 rounded-lg px-4 py-2 text-sm font-semibold transition-colors sm:min-w-32 ${
            activeMasterTab === "material"
              ? "bg-white text-[#0077C0] shadow-sm"
              : "text-gray-500 hover:bg-white/70 hover:text-gray-800"
          }`}
        >
          Material
        </button>
      </div>

      {activeMasterTab === "product" ? (
        <div
          role="tabpanel"
          id="product-transfer-panel"
          aria-labelledby="product-transfer-tab"
          className="space-y-6"
        >
          <header className="flex flex-col gap-3 border-b border-gray-200 pb-5 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <p className="text-sm font-semibold uppercase tracking-wide text-[#0077C0]">
                Warehouse
              </p>
              <h1 className="mt-1 text-2xl font-bold tracking-tight text-gray-900 sm:text-3xl">
                Product transfers
              </h1>
              <p className="mt-1 text-sm text-gray-500">
                Move products between warehouses and track pending transfers.
              </p>
            </div>
            <CreateTransferSection
              warehouseOptions={warehouseOptions}
              isAdmin={isAdmin}
              warehouseAssigned={warehouseAssigned}
              onSubmit={handleCreateBatch}
              loading={actionLoading}
            />
          </header>

          <section>
            <div
              className="mb-4 grid grid-cols-2 gap-2 rounded-xl border border-gray-200 bg-gray-50 p-1.5 sm:inline-flex sm:gap-1"
              role="tablist"
              aria-label="Product transfers"
            >
              <button
                type="button"
                role="tab"
                id="incoming-transfers-tab"
                aria-selected={activeTransferTab === "incoming"}
                aria-controls="incoming-transfers-panel"
                onClick={() => changeTransferTab("incoming")}
                className={`inline-flex min-h-12 items-center justify-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold transition-colors sm:px-5 ${
                  activeTransferTab === "incoming"
                    ? "bg-white text-[#0077C0] shadow-sm"
                    : "text-gray-500 hover:bg-white/70 hover:text-gray-800"
                }`}
              >
                <ArrowDownLeft size={17} className="shrink-0" />
                <span>Incoming</span>
                <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600">
                  {incoming.total}
                </span>
              </button>
              <button
                type="button"
                role="tab"
                id="outgoing-transfers-tab"
                aria-selected={activeTransferTab === "outgoing"}
                aria-controls="outgoing-transfers-panel"
                onClick={() => changeTransferTab("outgoing")}
                className={`inline-flex min-h-12 items-center justify-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold transition-colors sm:px-5 ${
                  activeTransferTab === "outgoing"
                    ? "bg-white text-[#0077C0] shadow-sm"
                    : "text-gray-500 hover:bg-white/70 hover:text-gray-800"
                }`}
              >
                <ArrowUpRight size={17} className="shrink-0" />
                <span>Outgoing</span>
                <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600">
                  {outgoing.total}
                </span>
              </button>
            </div>

            {activeTransferTab === "incoming" ? (
              <div
                role="tabpanel"
                id="incoming-transfers-panel"
                aria-labelledby="incoming-transfers-tab"
              >
                <IncomingTransferSection
                  incomingData={incoming}
                  allSerials={incomingSerials}
                  onAccept={handleAcceptBatch}
                  onPageChange={(page) =>
                    dispatch(fetchPendingIncomingTransfers(page))
                  }
                  loadingAction={actionLoading}
                  LoadingSpinner={LoadingSpinner}
                />
              </div>
            ) : (
              <div
                role="tabpanel"
                id="outgoing-transfers-panel"
                aria-labelledby="outgoing-transfers-tab"
              >
                <OutgoingTransferSection
                  outgoingData={outgoing}
                  allSerials={outgoingSerials}
                  onCancel={handleCancelBatch}
                  onPageChange={(page) =>
                    dispatch(fetchPendingOutgoingTransfers(page))
                  }
                  loadingAction={actionLoading}
                  LoadingSpinner={LoadingSpinner}
                />
              </div>
            )}
          </section>
        </div>
      ) : (
        <div
          role="tabpanel"
          id="material-transfer-panel"
          aria-labelledby="material-transfer-tab"
        >
          <MaterialTransferTab warehouseOptions={warehouseOptions} />
        </div>
      )}
    </div>
  );
};

export default StockTransferTab;
